import { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, QrCode } from 'lucide-react';
import QRCodeLib from 'qrcode';
import { toast } from 'sonner';

const FRONTEND_URL = window.location.origin;

export default function EventQRPrintout({ event, onClose }) {
  const canvasRef = useRef(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (event && canvasRef.current) {
      generateQRCode();
    }
  }, [event]);

  const generateQRCode = async () => {
    try {
      // Generate QR code URL that points to the event join page
      const joinUrl = `${FRONTEND_URL}/join/event/${event.event_id}`;
      
      // Get device pixel ratio for sharp rendering on high-DPI screens
      const scale = window.devicePixelRatio || 1;
      const qrSize = 300; // Base size
      
      await QRCodeLib.toCanvas(canvasRef.current, joinUrl, {
        width: qrSize * scale,
        margin: 4,
        errorCorrectionLevel: 'H', // High error correction for better scanning
        scale: scale,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      // Set canvas display size (prevents distortion)
      canvasRef.current.style.width = `${qrSize}px`;
      canvasRef.current.style.height = `${qrSize}px`;
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const handleDownloadPrintout = () => {
    if (!printRef.current || !canvasRef.current) return;

    // Create a new canvas for the complete printout
    const printCanvas = document.createElement('canvas');
    const ctx = printCanvas.getContext('2d');
    
    // Set printout dimensions (A4 size at 300 DPI: 2480 x 3508 pixels)
    printCanvas.width = 2480;
    printCanvas.height = 3508;
    
    // Fill white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, printCanvas.width, printCanvas.height);
    
    // Add border
    ctx.strokeStyle = '#F59E0B'; // amber-600
    ctx.lineWidth = 20;
    ctx.strokeRect(100, 100, printCanvas.width - 200, printCanvas.height - 200);
    
    // Title
    ctx.fillStyle = '#1F2937'; // gray-800
    ctx.font = 'bold 120px Playfair Display, serif';
    ctx.textAlign = 'center';
    ctx.fillText('Join Our Event!', printCanvas.width / 2, 400);
    
    // Event Name
    ctx.font = 'bold 100px Outfit, sans-serif';
    ctx.fillStyle = '#78350F'; // amber-900
    const eventNameLines = wrapText(ctx, event.event_name, printCanvas.width - 400);
    let yPosition = 600;
    eventNameLines.forEach((line) => {
      ctx.fillText(line, printCanvas.width / 2, yPosition);
      yPosition += 110;
    });
    
    // Event Description
    yPosition += 50;
    ctx.font = '60px Outfit, sans-serif';
    ctx.fillStyle = '#4B5563'; // gray-600
    const descLines = wrapText(ctx, event.event_description, printCanvas.width - 500);
    descLines.slice(0, 3).forEach((line) => {
      ctx.fillText(line, printCanvas.width / 2, yPosition);
      yPosition += 75;
    });
    
    // Generate high-quality QR code directly on the canvas
    yPosition += 100;
    const qrSize = 900; // Larger QR code for better scanning
    const qrX = (printCanvas.width - qrSize) / 2;
    
    // Create temporary canvas for high-res QR code
    const tempQRCanvas = document.createElement('canvas');
    const joinUrl = `${FRONTEND_URL}/join/event/${event.event_id}`;
    
    QRCodeLib.toCanvas(tempQRCanvas, joinUrl, {
      width: qrSize,
      margin: 4,
      errorCorrectionLevel: 'H',
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    }).then(() => {
      // Draw high-res QR code
      ctx.drawImage(tempQRCanvas, qrX, yPosition, qrSize, qrSize);
      yPosition += qrSize + 80;
      
      // Scan instruction
      ctx.font = 'bold 70px Outfit, sans-serif';
      ctx.fillStyle = '#F59E0B'; // amber-600
      ctx.fillText('Scan to Join!', printCanvas.width / 2, yPosition);
      yPosition += 100;
      
      // Event Details
      ctx.font = '50px Outfit, sans-serif';
      ctx.fillStyle = '#1F2937';
      const startDate = new Date(event.start_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      const endDate = new Date(event.end_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      ctx.fillText(`Event Period: ${startDate} - ${endDate}`, printCanvas.width / 2, yPosition);
      
      // Prize info
      if (event.prizes && event.prizes.length > 0) {
        yPosition += 80;
        ctx.fillText(`${event.prizes.length} Prize${event.prizes.length > 1 ? 's' : ''} Available`, printCanvas.width / 2, yPosition);
      }
      
      // Footer
      yPosition = printCanvas.height - 300;
      ctx.font = '45px Outfit, sans-serif';
      ctx.fillStyle = '#6B7280'; // gray-500
      ctx.fillText('DogtyDraw', printCanvas.width / 2, yPosition);
      yPosition += 60;
      ctx.font = '40px Outfit, sans-serif';
      ctx.fillText('Your Loyalty Rewards Platform', printCanvas.width / 2, yPosition);
      
      // Convert to image and download
      const dataUrl = printCanvas.toDataURL('image/png', 1.0); // Maximum quality
      const link = document.createElement('a');
      link.download = `${event.event_name.replace(/\s+/g, '_')}_QR_Printout.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success('Printout downloaded successfully!');
    }).catch(error => {
      console.error('Error generating QR for download:', error);
      toast.error('Failed to generate printout');
    });
  };

  // Helper function to wrap text
  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + ' ' + word).width;
      if (width < maxWidth) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };

  if (!event) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-playfair font-bold text-2xl text-primary">
            Event QR Printout
          </h2>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
          >
            Close
          </Button>
        </div>

        {/* Preview */}
        <div ref={printRef} className="bg-white border-4 border-amber-500 rounded-lg p-8 mb-6">
          <div className="text-center space-y-4">
            <h1 className="font-playfair font-bold text-4xl text-gray-800">
              Join Our Event!
            </h1>
            <h2 className="font-outfit font-bold text-3xl text-amber-900">
              {event.event_name}
            </h2>
            <p className="font-outfit text-lg text-gray-600 line-clamp-3">
              {event.event_description}
            </p>
            
            {/* QR Code */}
            <div className="bg-white p-6 inline-block border-4 border-gray-200 rounded-lg">
              <canvas 
                ref={canvasRef} 
                className="block" 
                style={{ maxWidth: '100%', height: 'auto', imageRendering: 'crisp-edges' }}
              />
            </div>
            
            <p className="font-outfit font-bold text-2xl text-amber-600">
              Scan to Join!
            </p>
            
            {/* Event Details */}
            <div className="space-y-2 text-gray-700">
              <p className="font-outfit text-base">
                <strong>Event Period:</strong> {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
              </p>
              {event.prizes && event.prizes.length > 0 && (
                <p className="font-outfit text-base">
                  <strong>{event.prizes.length} Prize{event.prizes.length > 1 ? 's' : ''} Available</strong>
                </p>
              )}
            </div>
            
            <div className="pt-4 border-t border-gray-200 mt-6">
              <p className="font-outfit text-sm text-gray-500">DogtyDraw</p>
              <p className="font-outfit text-xs text-gray-400">Your Loyalty Rewards Platform</p>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <Button
          onClick={handleDownloadPrintout}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
          size="lg"
        >
          <Download className="w-5 h-5 mr-2" />
          Download Printout
        </Button>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>💡 Tip:</strong> Download and print this QR code to display at your business location. 
            Customers can scan it to join this specific event!
          </p>
        </div>
      </div>
    </div>
  );
}
